import React, { useEffect, useRef } from 'react';
import { imageUrl } from '../lib/images';

export function Lightbox({ images, currentIndex, onClose, onNavigate }) {
  const touchStartX = useRef(null);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onNavigate((currentIndex - 1 + images.length) % images.length);
      if (e.key === 'ArrowRight') onNavigate((currentIndex + 1) % images.length);
    }
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [currentIndex, images.length, onClose, onNavigate]);

  if (!images || images.length === 0 || currentIndex < 0) return null;

  const current = images[currentIndex];
  const imgSrc = Array.isArray(current.billede) ? current.billede[0] : current.billede || current.url;

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (diff > 50) {
      onNavigate((currentIndex + 1) % images.length);
    } else if (diff < -50) {
      onNavigate((currentIndex - 1 + images.length) % images.length);
    }
    touchStartX.current = null;
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Billedvisning"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/90 backdrop-blur-md p-4 transition-all duration-300"
      onClick={onClose}
    >
      {/* Top action bar */}
      <div className="absolute top-4 right-4 z-50 flex items-center gap-3">
        <span className="text-xs tracking-wider text-slate-300 font-mono">
          {currentIndex + 1} / {images.length}
        </span>
        <button
          onClick={onClose}
          aria-label="Luk billedvisning"
          className="p-2 text-white/80 hover:text-white transition-colors cursor-pointer"
        >
          <i className="fa-solid fa-xmark text-xl" aria-hidden="true" />
        </button>
      </div>

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onNavigate((currentIndex - 1 + images.length) % images.length);
            }}
            aria-label="Forrige billede"
            className="absolute left-4 z-50 p-3 text-white/80 hover:text-white transition-colors cursor-pointer"
          >
            <i className="fa-solid fa-chevron-left text-2xl" aria-hidden="true" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onNavigate((currentIndex + 1) % images.length);
            }}
            aria-label="Næste billede"
            className="absolute right-4 z-50 p-3 text-white/80 hover:text-white transition-colors cursor-pointer"
          >
            <i className="fa-solid fa-chevron-right text-2xl" aria-hidden="true" />
          </button>
        </>
      )}

      {/* Image container */}
      <div
        className="max-w-5xl max-h-[85vh] w-full flex flex-col items-center justify-center"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="relative flex items-center justify-center w-full min-h-[300px] sm:min-h-[440px] max-h-[65vh] bg-slate-900/40 rounded-xl overflow-hidden">
          <img
            src={imageUrl(imgSrc, 'width=1000,quality=85,format=auto')}
            alt={current.title || 'Galleri billede'}
            className="max-h-[62vh] max-w-full w-auto object-contain rounded-lg shadow-2xl select-none mx-auto block"
            loading="eager"
            decoding="sync"
          />
        </div>

        {(current.title || current.beskrivelse) && (
          <div className="mt-4 text-center max-w-2xl text-white px-4">
            {current.title && (
              <h3 className="text-base font-semibold text-white tracking-tight">
                {current.title}
              </h3>
            )}
            {current.beskrivelse && (
              <p className="text-xs text-slate-300 mt-1 font-normal leading-relaxed">
                {current.beskrivelse}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Lightbox;
